/**
 * Pure data model + parsing for GitHub reference cards, with no React/Primer
 * dependencies. Kept separate from {@link ./GitHubReferenceCards} so the runtime
 * layer (e.g. client-tool definitions) can share the tool name and input parser
 * without pulling the UI component's Primer/octicon/CSS imports into its bundle.
 */

import {isGitHubControlledHost} from '../helpers/github-hosts'

/** The kind of GitHub entity a reference row points at. */
export type GithubReferenceType = 'pr' | 'issue' | 'commit' | 'repo' | 'discussion' | 'release'

/** The lifecycle state of a PR/issue reference, when applicable. */
export type GithubReferenceState = 'open' | 'closed' | 'merged' | 'draft'

/** A single GitHub reference to render as a card. */
export interface GithubReference {
  type: GithubReferenceType
  title: string
  /** Canonical https URL on github.com. Validated by {@link safeGithubUrl}. */
  url: string
  number?: number
  state?: GithubReferenceState
  /** `owner/name` slug the reference belongs to, e.g. `github/copilot`. */
  repo?: string
}

const GITHUB_REFERENCE_TYPES = new Set<GithubReferenceType>(['pr', 'issue', 'commit', 'repo', 'discussion', 'release'])

const GITHUB_REFERENCE_STATES = new Set<GithubReferenceState>(['open', 'closed', 'merged', 'draft'])

/**
 * Accept only `https://` URLs whose host is a GitHub-controlled domain
 * (github.com, ghe.com proxima tenants, or a subdomain of either — see
 * {@link isGitHubControlledHost}). Reference rows come from untrusted,
 * model-supplied tool input, so this blocks `javascript:`/`data:` URLs and
 * off-GitHub hosts from ever reaching an `<a href>`. Returns the normalized URL
 * string, or `null` when unsafe.
 */
export function safeGithubUrl(url: string): string | null {
  let parsed: URL
  try {
    // References come from arbitrary model-supplied input, not the current page,
    // so we must parse them as absolute URLs without a location-origin base.
    // eslint-disable-next-line no-restricted-syntax -- intentionally parse an arbitrary absolute URL
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:') return null
  if (!isGitHubControlledHost(parsed.hostname)) return null
  return parsed.toString()
}

/**
 * Coerce one raw, model-supplied reference row into a {@link GithubReference},
 * dropping rows that are missing a title/URL or whose URL is not a safe GitHub
 * link. Unknown `type` falls back to `repo`; unknown `state` is dropped.
 */
export function normalizeReference(raw: unknown): GithubReference | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const title = typeof r.title === 'string' ? r.title : undefined
  const rawUrl = typeof r.url === 'string' ? r.url : undefined
  if (!title || !rawUrl) return null
  const url = safeGithubUrl(rawUrl)
  if (!url) return null
  const type =
    typeof r.type === 'string' && GITHUB_REFERENCE_TYPES.has(r.type as GithubReferenceType)
      ? (r.type as GithubReferenceType)
      : 'repo'
  const rawState = typeof r.state === 'string' ? r.state.toLowerCase() : undefined
  const state =
    rawState && GITHUB_REFERENCE_STATES.has(rawState as GithubReferenceState)
      ? (rawState as GithubReferenceState)
      : undefined
  return {
    type,
    title,
    url,
    ...(typeof r.number === 'number' ? {number: r.number} : {}),
    ...(state ? {state} : {}),
    ...(typeof r.repo === 'string' ? {repo: r.repo} : {}),
  }
}

/** Normalize a raw `items` array (e.g. a tool call's parsed input) into references. */
export function normalizeGithubReferences(items: unknown): GithubReference[] {
  if (!Array.isArray(items)) return []
  const refs: GithubReference[] = []
  for (const raw of items) {
    const ref = normalizeReference(raw)
    if (ref) refs.push(ref)
  }
  return refs
}

/**
 * Name of the render-only, client-contributed tool the agent is instructed to
 * call in place of writing a markdown list of GitHub items. Shared so every
 * surface that renders an AHP timeline can special-case it identically.
 */
export const DISPLAY_GITHUB_REFERENCES_TOOL_NAME = 'display_github_references'

/**
 * Parse a `display_github_references` tool call's input (a JSON string or an
 * already-parsed object) into safe, normalized references. Returns `[]` for
 * malformed input so callers can render nothing rather than throw.
 */
export function parseGithubReferencesToolInput(toolInput: unknown): GithubReference[] {
  let parsed: unknown = toolInput
  if (typeof toolInput === 'string') {
    try {
      parsed = JSON.parse(toolInput)
    } catch {
      return []
    }
  }
  if (!parsed || typeof parsed !== 'object') return []
  return normalizeGithubReferences((parsed as {items?: unknown}).items)
}
